import { safe, state } from './config'
import { releaseForms } from './forms'
import { DIALOGS, dialogTitle, labelSource, notifyEffect } from './interactions'
import { isActive } from './journey'
import { maskText } from './scrub'
import { record } from './transport'

const CLOSE_WINDOW_MS = 700
const RECENT_TOAST_MS = 10000
const TOASTER_LOOKUPS_MS = [1000, 3000]

const open = new Map()
const manual = new Map()
const ignored = new WeakSet()
const watchedRoots = new WeakSet()
const seenToasts = new WeakSet()

let recentToasts = []

function dialogKind(el) {
  if (el.getAttribute('data-slot') === 'sheet-content') {
    return 'sheet'
  }

  if (el.querySelector('[data-slot="command"]')) {
    return 'command_palette'
  }

  return el.getAttribute('role') === 'alertdialog' ? 'alertdialog' : 'dialog'
}

function isShown(el) {
  const dataState = el.getAttribute('data-state')

  return dataState ? dataState === 'open' : el.getClientRects().length > 0
}

function recentCloseVia(el) {
  const now = Date.now()

  if (state.lastKey?.key === 'Escape' && now - state.lastKey.at <= CLOSE_WINDOW_MS) {
    return 'escape'
  }

  if (now - state.lastNavigationAt <= CLOSE_WINDOW_MS) {
    return 'navigation'
  }

  const pointer = state.lastPointer

  if (!el || !pointer || now - pointer.at > CLOSE_WINDOW_MS) {
    return null
  }

  return pointer.target && el.contains(pointer.target) ? 'button' : 'outside'
}

function resultFor(via) {
  if (via === 'escape' || via === 'outside') {
    return 'dismissed'
  }

  if (via === 'button' && state.lastPointer?.target?.closest?.('[data-slot$="-close"],[data-close-button]')) {
    return 'dismissed'
  }

  return via === 'navigation' ? 'navigated' : undefined
}

function closeDialog(el) {
  const entry = open.get(el)

  if (!entry) {
    return
  }

  open.delete(el)
  entry.observer?.disconnect()

  const via = recentCloseVia(el) ?? 'programmatic'

  record('ui.dialog', {
    target: entry.title,
    duration_ms: Date.now() - entry.openedAt,
    props: {
      action: 'close',
      kind: entry.kind,
      close_via: via,
      result: resultFor(via),
      destructive: entry.destructive,
      label_src: entry.labelSrc,
    },
  })
  releaseForms(el)
}

function openDialog(el) {
  if (open.has(el) || ignored.has(el) || el.closest('[data-reka-popper-content-wrapper]') || !isShown(el)) {
    return
  }

  // confirmAction's AlertDialog is recorded by recordDialog with its result instead.
  if (state.confirmOpen && el.getAttribute('role') === 'alertdialog') {
    ignored.add(el)

    return
  }

  const kind = dialogKind(el)
  const entry = {
    openedAt: Date.now(),
    kind,
    title: dialogTitle(el),
    labelSrc: labelSource(el, 'dialog'),
    destructive: kind === 'alertdialog' ? el.querySelector('[data-variant="destructive"]') !== null : undefined,
    observer: null,
  }

  try {
    entry.observer = new MutationObserver(
      safe(() => {
        if (el.getAttribute('data-state') === 'closed') {
          closeDialog(el)
        }
      })
    )
    entry.observer.observe(el, { attributes: true, attributeFilter: ['data-state'] })
  } catch {
    entry.observer = null
  }

  open.set(el, entry)
  notifyEffect('dialog')
  record('ui.dialog', {
    target: entry.title,
    props: { action: 'open', kind, destructive: entry.destructive, label_src: entry.labelSrc },
  })
}

function scan(node) {
  if (node.matches(DIALOGS)) {
    openDialog(node)
  }

  node.querySelectorAll(DIALOGS).forEach(openDialog)
}

function closeWithin(node) {
  Array.from(open.keys()).forEach((el) => {
    if (node === el || node.contains(el)) {
      closeDialog(el)
    }
  })
}

function watchRoot(root) {
  if (watchedRoots.has(root)) {
    return
  }

  watchedRoots.add(root)
  new MutationObserver(safe(onMutations)).observe(root, { childList: true })
}

// reka portals land directly in <body>; Headless UI portals land in #headlessui-portal-root.
function onMutations(mutations) {
  if (!isActive()) {
    return
  }

  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node instanceof Element) {
        if (node.id === 'headlessui-portal-root') {
          watchRoot(node)
        }

        scan(node)
      }
    })

    mutation.removedNodes.forEach((node) => {
      if (node instanceof Element) {
        closeWithin(node)
      }
    })
  })
}

function onToast(node) {
  if (!(node instanceof Element) || !node.hasAttribute('data-sonner-toast') || seenToasts.has(node)) {
    return
  }

  seenToasts.add(node)

  if (!isActive()) {
    return
  }

  const now = Date.now()
  const text = [node.querySelector('[data-title]')?.textContent, node.querySelector('[data-description]')?.textContent]
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()

  recentToasts = recentToasts.filter((toast) => now - toast.at < RECENT_TOAST_MS).concat({ text, at: now })
  notifyEffect('toast')
  record('outcome.toast', {
    target: node.getAttribute('data-type') || 'default',
    props: { text: maskText(text, 140) },
  })
}

function watchToaster(list) {
  if (watchedRoots.has(list)) {
    return
  }

  watchedRoots.add(list)
  new MutationObserver(
    safe((mutations) => mutations.forEach((mutation) => mutation.addedNodes.forEach(onToast)))
  ).observe(list, { childList: true })
  Array.from(list.children).forEach(onToast)
}

function findToasters() {
  document.querySelectorAll('ol[data-sonner-toaster]').forEach(watchToaster)
}

// The Toaster lives in V2Layout, which mounts asynchronously, so it is looked up again after each navigate.
export function lookupToasters() {
  findToasters()
  TOASTER_LOOKUPS_MS.forEach((delay) => window.setTimeout(safe(findToasters), delay))
}

export function toastSeen(probe, from, to) {
  return recentToasts.some((toast) => toast.at >= from && toast.at <= to && toast.text.includes(probe))
}

const recordNativeConfirm = safe((action, message, result, openedAt) => {
  if (!isActive()) {
    return
  }

  if (action === 'open') {
    notifyEffect('dialog')
  }

  const closing = action === 'close'

  record('ui.dialog', {
    target: maskText(typeof message === 'string' ? message : '', 80),
    duration_ms: closing ? Date.now() - openedAt : undefined,
    props: {
      action,
      kind: 'native_confirm',
      result: closing ? (result ? 'confirmed' : 'cancelled') : undefined,
      close_via: closing ? 'button' : undefined,
    },
  })
})

function wrapConfirm() {
  const original = window.confirm

  if (typeof original !== 'function') {
    return
  }

  window.confirm = function heliosConfirm(message) {
    const openedAt = Date.now()

    recordNativeConfirm('open', message)

    const result = original.call(window, message)

    recordNativeConfirm('close', message, result, openedAt)

    // Confirming is the person's action: the request that follows is theirs even though the dialog blocked for a while.
    if (result && state.lastTrigger) {
      state.lastTrigger = { ...state.lastTrigger, at: Date.now() }
    }

    return result
  }
}

export const recordDialog = safe((action, { kind = 'dialog', title, result, destructive, closeVia, el } = {}) => {
  if (kind === 'confirm') {
    state.confirmOpen = action === 'open'
  }

  if (!isActive()) {
    return
  }

  const element = el instanceof Element ? el : null
  const label = element?.closest('[data-helios-mask]')
    ? null
    : maskText(element?.getAttribute('data-helios-label') || title, 80)
  const labelSrc = element ? labelSource(element, 'dialog') : undefined
  const key = `${kind}|${label ?? ''}`

  if (action === 'open') {
    manual.set(key, Date.now())
    notifyEffect('dialog')
    record('ui.dialog', { target: label, props: { action: 'open', kind, destructive, label_src: labelSrc } })

    return
  }

  const openedAt = manual.get(key)
  const recent = recentCloseVia(null)
  const via = recent ?? closeVia ?? undefined

  manual.delete(key)
  record('ui.dialog', {
    target: label,
    duration_ms: openedAt ? Date.now() - openedAt : undefined,
    props: {
      action: 'close',
      kind,
      close_via: via,
      result: result ?? resultFor(via),
      destructive,
      label_src: labelSrc,
    },
  })
  releaseForms(element)
})

export function installOverlays() {
  watchRoot(document.body)

  const portalRoot = document.getElementById('headlessui-portal-root')

  if (portalRoot) {
    watchRoot(portalRoot)
  }

  wrapConfirm()
  lookupToasters()
}
